import { resultHandler, handler, ISortByCol, ISortingOptions, IRowData } from "../Interfaces";
import { type CoreFrameGrid } from "../index";
import { Constants } from "../Constants";
import { CellDescriptionTooltipHelper } from "./CellDescriptionTooltipHelper";

export enum SortState {
    ASC = "ASC",
    DESC = "DESC"
}

export class EventHelper {
    private DOUBLECLICK_TIMER: any;
    private DOUBLECLICK_DELAY = 200;
    private DOUBLECLICK_PREVENT = false;

    private isResizing = false;
    private isScrolling = false;

    private tooltipHelper: CellDescriptionTooltipHelper;

    // Hilfsvariable für Shift-Klick
    private lastRowClickedWithoutShiftID: any = undefined;
    private lastRowClickedWithoutShiftIndex: number = undefined;

    // Handler
    private onSelectionChangeHandler: (data: Array<any>) => void;
    private onSortChangeHandler: (sort: ISortByCol[]) => void;
    private onDblClickHandler: (id: any, rowData: any, ev?: JQuery.DoubleClickEvent) => any;
    private onMouseOverHandler: (id: any, rowData: any, ev?: JQuery.MouseOverEvent) => any;
    private onMouseOutHandler: () => any;
    private onMouseOverColumn: (columnText?: string) => any;
    private onKeyDownHandler: (id: any, rowData: any, ev?: JQuery.KeyDownEvent) => any;
    private onSaveHandler: resultHandler;
    private onDeleteHandler: resultHandler;
    private onAddHandler: handler;
    private onBeforeEditHandler: any;
    private onFocusLostHandler: (ev?: JQuery.MouseUpEvent) => void;

    constructor(private grid: CoreFrameGrid, handler: any) {
        const tooltipOptions = this.grid.getOptions().tooltip;
        this.tooltipHelper = new CellDescriptionTooltipHelper(tooltipOptions.openingDelayMs, tooltipOptions.closingDelayMs);

        this.onSelectionChangeHandler = handler.onSelectionChangeHandler;
        this.onSortChangeHandler = handler.onSortChangeHandler;
        this.onDblClickHandler = handler.onDblClickHandler;
        this.onMouseOverHandler = handler.onMouseOverHandler;
        this.onMouseOutHandler = handler.onMouseOutHandler;
        this.onMouseOverColumn = handler.onMouseOverColumn;
        this.onKeyDownHandler = handler.onKeyDownHandler;
        this.onSaveHandler = handler.onSaveHandler;
        this.onDeleteHandler = handler.onDeleteHandler;
        this.onAddHandler = handler.onAddHandler;
        this.onBeforeEditHandler = handler.onBeforeEditHandler;
        this.onFocusLostHandler = handler.onFocusLostHandler;
    }

    public registerAll() {
        // Selektieren einer Zeile
        this.grid.getBody().$tbody.on(Constants.EVENT_CLICK, 'tr', this.clickRow.bind(this));
        // Doppelklick auf eine Zeile
        this.grid.getBody().$tbody.on(Constants.EVENT_DBLCLICK, 'tr', this.doubleClickRow.bind(this));
        // Mouseover
        this.grid.getBody().$tbody.on(Constants.EVENT_MOUSEOVER, 'tr', this.mouseOverRow.bind(this));
        // MouseOut
        this.grid.getBody().$tbody.on(Constants.EVENT_MOUSEOUT, 'tr', this.mouseOutRow.bind(this));
        // Mouseover column
        this.grid.getBody().$tbody.on(Constants.EVENT_MOUSEOVER, 'td', this.mouseOverTableCell.bind(this));
        // Mouseleave column
        this.grid.getBody().$tbody.on(Constants.EVENT_MOUSEOUT, 'td', this.mouseLeavingTableCell.bind(this));
        // Tastaturnavigation
        this.grid.getWrapper().on(Constants.EVENT_KEYDOWN, this.wrapperKeyDown.bind(this));

        if (this.grid.getOptions().isHeightUserAdjustable)
            this.grid.getResizingElement()?.on(Constants.EVENT_DBLCLICK, this.handleResizerDblClick.bind(this));

        // Inline-Editing
        this.grid.getBody().$tbody.on(Constants.EVENT_CLICK, '.' + Constants.CLASS_ROW_EDITBUTTON, this.editClick.bind(this));
        this.grid.getBody().$tbody.on(Constants.EVENT_CLICK, '.' + Constants.CLASS_ROW_DELETEBUTTON, this.deleteClick.bind(this));
        this.grid.getBody().$tbody.on(Constants.EVENT_CLICK, '.' + Constants.CLASS_ROW_SAVEBUTTON, this.saveClick.bind(this));
        this.grid.getWrapper().on(Constants.EVENT_CLICK, '.' + Constants.CLASS_ADDBUTTON, this.addClick.bind(this));
        this.registerOuterClick();

        // Sortierung
        this.grid.getHeader().$thead.on(Constants.EVENT_CLICK, 'th.sortable', this.sortableHeaderClick.bind(this));

        // Vertikales Scrollen des Header
        this.grid.getBody().getWrapper().on(Constants.EVENT_SCROLL, this.tableBodyScroll.bind(this));
    }

    public registerVerticalGridResizing() {
        const $gridWrapper = this.grid.getWrapper();
        const $bottomBorderResizer = $gridWrapper.find(`+ .${Constants.CLASS_BOTTOM_BORDER_RESIZER}`);
        const $body = $(document.body);

        $bottomBorderResizer.on(Constants.EVENT_MOUSEDOWN, mouseDownEvent => {
            // Eigentliches Event unterbinden. So wird beispielsweise verhindert, dass bei Selektion von Elementen des
            // Grids diese gezogen werden können und dadurch das Resizing beeinträchtigen.
            mouseDownEvent.preventDefault();

            this.isResizing = true;
            $body.addClass(Constants.CLASS_VERTICAL_RESIZE_CURSOR);

            // Events werden erst beim Drag-Start registriert und bei MOUSEUP wieder entfernt,
            // damit bei vielen Grid-Instanzen nicht dauerhaft Handler auf dem Body aktiv sind.
            $body.on(Constants.EVENT_MOUSEUP + this.grid.id, () => {
                this.isResizing = false;
                $body.removeClass(Constants.CLASS_VERTICAL_RESIZE_CURSOR);
                $body.off(Constants.EVENT_MOUSEUP + this.grid.id);
                $body.off(Constants.EVENT_MOUSEMOVE + this.grid.id);
            });

            $body.on(Constants.EVENT_MOUSEMOVE + this.grid.id, mouseMoveEvent => {
                mouseMoveEvent.preventDefault();
                mouseMoveEvent.stopPropagation();

                const mouseY = mouseMoveEvent.clientY;
                const gridOffsetTop = $gridWrapper.offset().top;
                const newHeight = Math.round(mouseY - gridOffsetTop);
                this.grid.setHeight(`${newHeight}px`);
            });
        });
    }

    public isGridResizeDragActive(): boolean {
        return this.isResizing;
    }

    public registerOnHtmlBodyClick(callback: () => void) : void{
        $(document.body).on(Constants.EVENT_CLICK + this.grid.id, callback);
    }

    private registerOuterClick(): void{
        let mousedown = null;

        // Nur wenn auf dem selbem Element MouseDown und MouseUp durchgeführt wurde
        // soll ein Klick ausgewertet werden
        // Usecase: Markieren einer Zeile und versehentliches Verrutschen des Cursors nach unten
        // führte zum Schließen des Bearbeitungsmodus

        const prevent = (ev: JQuery.MouseDownEvent) => {
            this.isScrolling = this.isClickOnScrollbar(ev);
            mousedown = ev.target;
        };

        const callback = (ev: JQuery.MouseUpEvent) => {
            if (ev.target != mousedown || this.isScrolling){
                this.isScrolling = false;
                return;
            }

            this.outerClick(ev)
        };

        this.grid.getWrapper().on(Constants.EVENT_MOUSEDOWN, prevent);
        this.grid.getWrapper().on(Constants.EVENT_MOUSEUP, callback);

        this.registerOnHtmlBodyClick(this.outerClick.bind(this));
    }

    public dispose() : void {
        this.tooltipHelper.dispose();

        const $body = $(document.body);

        // Verlassen des Edit-Mode etc.
        $body.off(Constants.EVENT_CLICK + this.grid.id);

        // Vertical Resizing
        $body.off(Constants.EVENT_MOUSEDOWN + this.grid.id);
        $body.off(Constants.EVENT_MOUSEUP + this.grid.id);
        $body.off(Constants.EVENT_MOUSEMOVE + this.grid.id);
        
        // Tooltip
        $body.off(Constants.EVENT_MOUSEOUT + this.grid.id);
        $body.off(Constants.EVENT_MOUSEOVER + this.grid.id);

        this.grid.getResizingElement()?.off(Constants.EVENT_DBLCLICK);
    }

    /**
     * Ermittelt, ob ein Klick auf der Scrollbar ausgeführt wurde.
     *
     * @remarks
     * Firefox mit aktiver Option widget.windows.overlay-scrollbars.enabled (eine Firefox-Einstellung,
     * nicht zu verwechseln mit dem npm-Paket OverlayScrollbars) rendert die Scrollbar
     * über dem Inhalt und belegt keinen eigenen Platz (clientWidth === offsetWidth), daher kann
     * die Methode den Klick nicht geometrisch erkennen. In diesem Fall wird das tatsächliche Scrollen
     * über das isScrolling-Flag erfasst, das im scroll-Event (tableBodyScroll) gesetzt wird.
     * Bekannte Einschränkung: Ein Klick auf die Scrollbar ohne tatsächlichen Scroll
     * (z.B. bereits am Ende der Liste) wird nicht erkannt und beendet den Bearbeitungsmodus.
     * Eine zuverlässige Erkennung würde eine grundlegende Umstellung der DOM-Elemente bzw. ihres
     * Padding/Margin erfordern; der Kosten-/Nutzen-Faktor ist nicht gegeben.
     */
    private isClickOnScrollbar(event: JQuery.MouseDownEvent): boolean {
        const $wrapper = this.grid.getBody().getWrapper();
        const offsetLeft = $wrapper.offset().left;
        const offsetTop = $wrapper.offset().top;
        const width = $wrapper[0].clientWidth;
        const height = $wrapper[0].clientHeight;

        // untere Scrollbar
        const clickedOnBottom = event.clientY >= offsetTop + height;
        const clickedOnRight = event.clientX >= offsetLeft + width;

        return clickedOnBottom || clickedOnRight;
    }

    private clickRow(ev) {
        // Wenn mittlere Maustaste gedrueckt wird, wie Doppelklick mit Strg verarbeiten
        if(ev.button === 1) {
            ev.ctrlKey = true;
            this.doubleClickRow(ev);
            return;
        }

        this.DOUBLECLICK_TIMER = setTimeout(() => {
            if (!this.DOUBLECLICK_PREVENT) {
                const tr = ev.currentTarget as HTMLTableRowElement;
                // Falls Element nicht mehr im DOM ist, abbrechen
                if(!$.contains(document.body, tr)){
                    return;
                }
                // Normaler Klick, auch mit STRG möglich für Mehrfachselektion
                // ODER: Noch keine Zeile bisher selektiert: Daher wie ein normaler Klick
                if (!ev.shiftKey || (ev.shiftKey && this.lastRowClickedWithoutShiftIndex == undefined)) {
                    const rowID = tr.dataset[Constants.DATA_ATTRIBUTE_ROW_ID];
                    this.lastRowClickedWithoutShiftID = rowID;
                    this.lastRowClickedWithoutShiftIndex = this.grid.getIndexByRowId(rowID);
                    if (tr.classList.contains(Constants.CLASS_ROW_SELECTED) && ev.ctrlKey)
                        this.grid.deselectRowElement(tr);
                    else
                        this.grid.selectRowElement(tr, ev.ctrlKey, !ev.ctrlKey);
                }
                // Klick mit Shift
                else {
                    const clickedID = tr.dataset[Constants.DATA_ATTRIBUTE_ROW_ID];
                    const indexOfClickedRow = this.grid.getIndexByRowId(clickedID);

                    const startIndex = (indexOfClickedRow >= this.lastRowClickedWithoutShiftIndex ? this.lastRowClickedWithoutShiftIndex : indexOfClickedRow);
                    let endIndex = (indexOfClickedRow < this.lastRowClickedWithoutShiftIndex ? this.lastRowClickedWithoutShiftIndex : indexOfClickedRow);
                    if (endIndex >= this.grid.getRows().length)
                        endIndex = (this.grid.getRows().length - 1);

                    const ids = [];
                    for (let x = startIndex; x < endIndex + 1; x++) {
                        ids.push(this.grid.getRows()[x][this.grid.getOptions().rowIdName]);
                    }

                    this.grid.selectRowByIds(ids, true, true);
                }
                this.fireSelectionChangedHandler();
            }
            this.DOUBLECLICK_PREVENT = false;
        }, this.DOUBLECLICK_DELAY);
    }

    private doubleClickRow(ev) {
        clearTimeout(this.DOUBLECLICK_TIMER);
        this.DOUBLECLICK_PREVENT = true;

        if (this.grid.getOptions().doubleClickToEdit)
            this.editClick(ev)
        else if (typeof this.onDblClickHandler == "function") {
            const $tr = $(ev.currentTarget).closest('tr');
            this.grid.selectRowElement($tr[0], false, true);

            const rowData = this.grid.getRowData($tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
            const id = $tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

            this.fireSelectionChangedHandler();

            this.onDblClickHandler(id, rowData, ev);
        }
    }

    private mouseOverRow(ev) {
        if (typeof this.onMouseOverHandler == "function") {
            const $tr = $(ev.currentTarget).closest('tr');

            const rowData = this.grid.getRowData($tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
            const id = $tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

            this.onMouseOverHandler(id, rowData, ev);
        }
    }

    private mouseOutRow(ev) {
        if (typeof this.onMouseOutHandler == "function") {
            this.onMouseOutHandler();
        }
    }

    private mouseOverTableCell(ev) {
        const target = ev.target;

        if (this.grid.getOptions().tooltip.show)
            this.tooltipHelper.createTooltip(target.textContent, ev.target, ev);

    }

    private mouseLeavingTableCell(ev) {
        if (this.grid.getOptions().tooltip.show)
            this.tooltipHelper.disposeTooltip(ev.target);
    }

    private editClick(ev) {
        if (!this.grid.isEditingAllowed())
            return;

        const $tr = $(ev.currentTarget).closest('tr');

        const data = this.grid.getRowData($tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
        const id = $tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

        if (typeof this.onBeforeEditHandler == "function") {
            const handlerResult = this.onBeforeEditHandler(data);
            if (handlerResult === true) {
                this.grid.enterEditModeById(id);
            }
            else if (this.isPromise(handlerResult)) {
                handlerResult.then((enterEditMode) => {
                    if (enterEditMode)
                        this.grid.enterEditModeById(id);
                });
            }
        }
        else if (this.isPromise(this.onBeforeEditHandler)) {
            this.onBeforeEditHandler(data).then((enterEditMode: boolean) => {
                if (enterEditMode)
                    this.grid.enterEditModeById(id);
            });
        }
        else
            this.grid.enterEditModeById(id);
    }

    private isPromise(handler: any): boolean{
        return typeof handler == "object" && typeof handler.then == "function";
    }

    private deleteClick(ev) {
        if (typeof this.onDeleteHandler == "function") {
            const $tr = $(ev.currentTarget).closest('tr');
            this.grid.selectRowElement($tr[0], false, true);

            const data = this.grid.getRowData($tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
            const id = $tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

            const result = this.onDeleteHandler(data);
            if (typeof result == "object" && typeof result.then == "function")
                result.then((deleteSucces) => {
                    if(deleteSucces) {
                        this.grid.resetWrapperClasses()
                        this.removeRowFromDataset(id);
                    }
                });
            else if (result)
                this.grid.resetWrapperClasses();
        }
    }

    private removeRowFromDataset(rowId: string) {
        this.grid.getRowDataSet().forEach(entry => {
            if (entry[Constants.DATA_ATTRIBUTE_ROW_ID] === rowId) {
                this.grid.getRowDataSet().delete(entry);
            }
        });
    }

    private saveClick(ev) {
        const $tr = $(ev.currentTarget).closest('tr');
        if ($tr?.length > 0) {
            this.grid.selectRowElement($tr[0], false, true);

            const data = this.grid.getRowData($tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
            const id = $tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

            if (typeof this.onSaveHandler == "function") {
                const result = this.onSaveHandler(data);

                if (typeof result == "object" && typeof result.then == "function")
                    result.then((saveSuccess) => {
                        if(saveSuccess)
                            this.grid.getBody().leaveEditModeFor($tr[0]);
                    });
                else if (result)
                    this.grid.getBody().leaveEditModeFor($tr[0]);
            }
        }
    }

    private handleResizerDblClick() {
        this.grid.onResizerDblClick();
    }

    private addClick(ev) {
        if (!this.grid.isAddingAllowed())
            return;
        if (typeof this.onAddHandler == "function")
            this.onAddHandler();
    }

    private outerClick(ev: JQuery.MouseUpEvent) {
        if(typeof this.onFocusLostHandler == "function")
            this.onFocusLostHandler(ev);

        if(ev.isPropagationStopped())
            return;
        
        const $target = $(ev.target);
        if (this.grid.getOptions().applyOnLostFocus) {
            const $editRows = this.grid.getRowsInEditMode();
            // Wenn die aktuell bearbeitete Zeile (die zu diesem Grid gehört) angeklickt wird, oder sich das Grid nicht im EditModus befindet, tue nichts
            if (($.contains(this.grid.getWrapper().get(0), $target.get(0)) && $target.closest('.' + Constants.CLASS_ROW).length > 0)
                || !this.grid.getWrapper().hasClass(Constants.CLASS_ISEDITING))
                return;
            else {
				// Beim Verschieben der Spalten nichts machen
                if($target.is('.' + Constants.CLASS_RESIZER) || $target.is(this.grid.getHeader().getWrapper()))
                    return;

                const editOptions = this.grid.getOptions().isEditable;
                if((editOptions === true || (typeof editOptions != "boolean" && editOptions.saveOnFocusLost !== false)))
                    this.saveClick({ currentTarget: $editRows[0] });
            }
        }
    }

    private sortableHeaderClick(event: JQuery.ClickEvent) {
        // Im Editiermodus darf nicht umsortiert werden
        if (this.grid.getWrapper().hasClass(Constants.CLASS_ISEDITING))
            return;

        let sortExpression: ISortByCol[];

        const options = this.grid.getOptions();

        if(options.sortingClickStrategy === 'kendo')
            sortExpression = this.sortByKendoStrategy(event);
        else if(options.sortingClickStrategy === 'desktop')
            sortExpression = this.sortByDesktopStrategy(event);

        this.grid.getHeader().setSortExpression(sortExpression);

        if (typeof this.onSortChangeHandler === "function") {
            this.onSortChangeHandler(this.grid.getHeader().getSortExpression());
        }
    }

    private sortByKendoStrategy(event: JQuery.ClickEvent): ISortByCol[] {
        let sortExpression: ISortByCol[];
        const options = this.grid.getOptions();
        const $th = $(event.currentTarget);
        const old = $th.attr('data-' + options.sortDataName);

        // Wenn nicht sortierbar
        if(options.isSortable === false)
            return [];

        // Bei Ctrl wird die Spalte der aktuellen Sortierung hinzugefügt (MultiColumnSort)
        if (event.ctrlKey)
            sortExpression = this.grid.getHeader().getSortExpression();
        else
            sortExpression = [];

            
        // Wenn Mehrfachsortierung aus, bisherige Sortierung verwerfen
        if(!this.isMultiColumnSortEnabled()){
            sortExpression = [];
        }

        // Für das Feld wird die alte Sortierung entfernt
        if (old) {
            sortExpression = sortExpression.filter((value) => {
                return value.field != $th.data('sortfield');
            })
        }

        if (!old || old == SortState.DESC) {
            sortExpression.push({
                field: $th.data('sortfield'),
                dir: SortState.ASC
            });
        }
        else {
            sortExpression.push({
                field: $th.data('sortfield'),
                dir: SortState.DESC
            });
        }

        return sortExpression;
    }

    private isMultiColumnSortEnabled(): boolean {
        const options = this.grid.getOptions();

        if(options.isSortable === false || (options.isSortable as ISortingOptions).mode !== 'multiple')
            return false;

        return true;
    }

    private sortByDesktopStrategy(event: JQuery.ClickEvent): ISortByCol[] {
        let sortExpression: ISortByCol[] = this.grid.getHeader().getSortExpression();
        const options = this.grid.getOptions();
        const $th = $(event.currentTarget);
        const sortField = $th.data('sortfield');

        // Wenn nicht sortierbar
        if(options.isSortable === false)
            return [];

        // Wenn Mehrfachsortierung aus, bisherige Sortierung verwerfen
        // aber nur, wenn nach einem anderen Feld als dem neuen sortiert wird
        if(!this.isMultiColumnSortEnabled() && !this.isSortedOnlyBySortfield(sortExpression, sortField)){
            sortExpression = [];
        }

        // Bisherige Sortierung
        const existingSorting = sortExpression.filter(val => val.field === sortField);

        // Wenn nach dem Feld bereits sortiert wird
        if (existingSorting.length > 0) {
            const existingIndex = existingSorting[0].index;
            const existingDirection = existingSorting[0].dir;

            // In jedem Fall wird das Sortierkriterium vorerst entfernt
            sortExpression = sortExpression.filter((value) => {
                return value.field !== sortField;
            });

            // Wenn es das vorderste Kriterium ist, dann wird es angepasst
            if (existingIndex === 0) {
                const isSortable = options.isSortable;
                const unsort = typeof isSortable !== 'boolean' && isSortable.allowUnsort && existingDirection as SortState === SortState.DESC;
                if (!unsort) {
                    sortExpression.splice(0, 0, {
                        field: sortField,
                        dir: this.invertSortState(existingDirection as SortState)
                    });
                }
            } // anderenfalls wir die Sortierung zurückgesetzt und die Spalte wird als Einzige hinzugefügt 
            else {
                sortExpression = [{
                    field: sortField,
                    dir: SortState.ASC
                }];
            }
        }
        else {
            sortExpression.splice(0, 0, {
                field: sortField,
                dir: SortState.ASC
            });
        }

        return sortExpression;
    }

    /**
     * Gibt zurück, ob NUR nach dem übergebenen Feld bereits sortiert wird
     * @param sortExpression 
     * @param sortfield 
     */
    private isSortedOnlyBySortfield(sortExpression: ISortByCol[], sortfield: string): boolean {
        if (sortExpression.length != 1) {
            return false;
        }

        return sortExpression[0].field === sortfield;
    }

    private invertSortState(state: SortState): SortState{
        if(state === SortState.ASC){
            return SortState.DESC;
        }
        else {
            return SortState.ASC;
        }
    }

    /**
     * Handles the "scroll"-Event by synchronizing selected elements' "scrollLeft"
     * property to the target's "scrollLeft" property the scroll occurred on.
     * @param ev The event
     * @param customTarget A custom target element that contains the relevant 
     * scroll information (externally used!)
     */
    private tableBodyScroll(ev, customTarget?: HTMLElement) {
        this.isScrolling = true;

        const target = customTarget || ev.currentTarget;
        const elements = [
            this.grid.getHeader()?.getWrapper()[0],
            this.grid.getTotalRow()?.getWrapper()[0],
        ].filter(e => e != null);
        for (const element of elements)
            element.scrollLeft = target.scrollLeft;
    }

    public resetShiftKlick() {
        this.setParameterForShiftKlick(undefined, undefined);
    }

    public setParameterForShiftKlick(id: any, index: number) {
        this.lastRowClickedWithoutShiftID = id;
        this.lastRowClickedWithoutShiftIndex = index;
    }

    private wrapperKeyDown(ev) {
        let newSelected = undefined;
        switch (ev.keyCode) {
            // Pfeil nach oben
            case 38:
                ev.preventDefault();
                newSelected = this.grid.selectPrevious();
                if (newSelected != null) {
                    this.setParameterForShiftKlick(newSelected.id, newSelected.index);
                    this.fireSelectionChangedHandler();
                }
                break;
            // Pfeil nach unten
            case 40:
                ev.preventDefault();
                newSelected = this.grid.selectNext();
                if (newSelected != null) {
                    this.setParameterForShiftKlick(newSelected.id, newSelected.index);
                    this.fireSelectionChangedHandler();
                }
                break;
            // A für Strg+A
            case 65:
                if (ev.ctrlKey && ev.target.tagName != 'TEXTAREA' && ev.target.tagName != 'INPUT') {
                    ev.preventDefault();
                    this.grid.selectAll();
                    this.fireSelectionChangedHandler();
                }
                break;
            default:
                if (typeof this.onKeyDownHandler == "function") {
                    const $rows = this.grid.getSelectedRows();
                    if ($rows.length == 0)
                        return;

                    const $tr = $($rows[0]);
                    const rowData = this.grid.getRowData($tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID]);
                    const id = $tr[0].dataset[Constants.DATA_ATTRIBUTE_ROW_ID];

                    this.onKeyDownHandler(id, rowData, ev);
                }
        }
    }

    public fireSelectionChangedHandler(): void {
        if (typeof this.onSelectionChangeHandler == "function") {
            const selectedData = this.grid.getSelectedData();
            this.onSelectionChangeHandler(selectedData);
        }
    }
}
